/**
 * Sheet — the rows-side model of one sheet, as its composition root. Its one
 * responsibility of its own is ROW MEMBERSHIP: which rows the sheet holds
 * (`addRow` / `deleteRow`, hydrate's `withRows`, the row list, the by-id map,
 * the dependency-graph nodes) and the wiring of the units that own the rest —
 * cell loads (`SheetCellLoads`, `SheetPrefetch`), the row indices
 * (`SheetRowIndex`), row-field writes (`SheetRowFields`), alias re-expansion
 * and forks (`SheetAliasReexpansion`, `SheetAliasFork`), surfaces
 * (`SheetSurfaces`), the renderer copy-on-write (`SheetRendererCow`), the
 * recompute queue and the promotion flush — each built here over a narrow
 * host of this class's private members.
 *
 * Owns the internal stores (tree,
 * cell data, row metadata, alias instances), a shared
 * `InvalidationSink` that every store emits through, the formula
 * dependency graph, and the Backend used to fetch + sync. Row
 * classes hold a reference to a Sheet and access its stores through
 * typed accessor methods.
 *
 * Construction: `RawSheet.hydrate(raw, deps)` (`../raw/raw-sheet.ts`) is
 * the canonical entry point; it calls `Sheet.withRows` to resolve
 * the cyclical `ModelRow.sheet ↔ Sheet.rootRows` reference.
 *
 * Consumers subscribe to the Sheet's invalidation stream via
 * `sheet.invalidationSink.subscribe(cb)` and filter by
 * `Invalidation.matches(event, path)` or event-class instanceof.
 */

import type { Option } from "../core/option.ts";
import { SheetId } from "../ids/sheet-id.ts";
import { SheetKeyRef } from "../ids/sheet-key-ref.ts";
import { Json } from "../core/json.ts";
import type { Timescale } from "../render/timescale.ts";
import { NaiveDate } from "@tento-chrono";
import {
  DependencyGraph,
  type DependencyGraphReads,
} from "@tento-core/dependency-graph";

import { Tracing } from "@tento-core/tracing";

import {
  type Backend,
  type BatchRowCells,
  BatchFetchThrottledError,
  type SurfaceKind,
} from "../backends/rows-backend.ts";
import { IngestionGate } from "../sync/ingestion-gate.ts";
import { InstallPacer } from "../core/install-pacer.ts";
import {
  type AliasTemplate,
  type BuiltinAliasFactory,
  AliasTemplateOps,
  type AliasExpansionPlan,
  type AliasLocalState,
} from "../aliases/alias-templates.ts";
import { isCanonicalKey } from "../backends/canonical-backend.ts";
import type { CellCardinality, CellEntry, CellValue } from "../core/dtype.ts";
import { EncodingRegistry } from "../core/encoding-registry.ts";
import { FacadeRegistry } from "../facades/facade.ts";
import { RowFacade } from "../facades/row-facade.ts";
import { FormulaEngine, copyFormulaGraph } from "../formula/formula-engine.ts";
import { FormulaCoverage } from "../formula/formula-coverage.ts";
import { ShapeRegistry } from "../core/shape-registry.ts";
import {
  Actor,
  ColumnDataChanged,
  type InvalidationSink,
  NewRowInserted,
  RowDataChanged,
  RowLegendChanged,
  RowRangeCovered,
  RowRangeDataChanged,
  SheetStructureChanged,
  SheetMetadataChanged,
  SheetWidthChanged,
} from "../tx/invalidation.ts";
import {
  descriptorOf,
  reconcileRowMetadata,
  rowLocalIdOf,
  type RowSnapshotSource,
  snapshotRow,
} from "./sheet-row-codec.ts";
import type { RawRow } from "../wire/raw-sheet-wire.ts";
import type { AddRowSpec, RowCtor, ModelSheet, SheetLocalCellFeed, SheetStores } from "./rows-sheet.ts";
import type { SheetRegistries } from "./sheet-registries.ts";
import { RowTypeSpecOps } from "../rows/row-type-spec.ts";
import { RowEnvelopes } from "../rows/row-envelopes.ts";
import { RowId } from "../ids/row-id.ts";
import { RowLocalId } from "../ids/row-local-id.ts";
import type { ModelRow } from "../rows/model-row.ts";
import type { RenderCursors } from "../render/render-cursor.ts";
import type { SceneNodeStamp } from "../stores/scene-node-stamp.ts";
import {
  installSheetFriend,
  type ListenerDrainObserver,
  type RecomputeSweepObserver,
  type SheetInternals,
  type SheetWiring,
} from "./sheet-stores.ts";
import type { Unsubscribe } from "../backends/rows-backend.ts";
import {
  RowMetadata,
} from "../stores/index.ts";
import type { SyncPoll } from "../sync/sync-state.ts";
import { SyncKeyWatcher } from "../sync/sync-watcher.ts";
import { SheetCellLoads } from "./sheet-cell-loads.ts";
import { SheetPrefetch } from "./sheet-prefetch.ts";
import { SheetRowIndex } from "./sheet-row-index.ts";
import { REEXPAND_INPUT_SIGNATURE, type ReexpandCause, SheetAliasReexpansion } from "./sheet-alias-reexpansion.ts";
import { type RecomputeScheduler, RecomputeTally, SheetRecomputeQueue } from "./sheet-recompute-queue.ts";
import { SheetPromotionFlush } from "./sheet-promotion-flush.ts";
import { flagRefused, SheetRendererCow } from "./sheet-renderer-cow.ts";
import { SheetAliasFork } from "./sheet-alias-fork.ts";
import { SheetRowFields } from "./sheet-row-fields.ts";
import { SheetSurfaces, surfaceAnchorOf } from "./sheet-surfaces.ts";
import { CellWriteCoalescer } from "../stores/cell-write-coalescer.ts";
import { columnDataChangedOf } from "../stores/source-cell-data.ts";
import { CellWrites } from "../ledger/cell-writes.ts";
import { ComputedDataSource } from "../rows/variants/computed-data-source.ts";
import { FormulaDataSource } from "../rows/variants/formula-data-source.ts";
import {
  hydrateDefaultRenderer,
  shapeFromMeta,
} from "../rows/variants/hydrate-default-renderer.ts";
import { AliasRow } from "../rows/variants/alias-row.ts";
import { parseRow } from "../rows/variants/registry.ts";
import {
  observeSourceInstalls,
  SourceRow,
} from "../rows/variants/source-row.ts";
import { SdkRequestError } from "../core/errors.ts";
import { CellId } from "../ids/ids.ts";
import { SheetNetworkKey } from "../ids/sheet-network-key.ts";
import { RowNetworkKey } from "../ids/row-network-key.ts";
import type { RowWrites } from "../ledger/row-writes.ts";
import { cellBearingRow } from "../formula/model-formula.ts";

export type { AddRowSpec, RowCtor, ModelSheet, SheetLocalCellFeed, SheetStores } from "./rows-sheet.ts";
export type { ReexpandCause, ReexpandMode } from "./sheet-alias-reexpansion.ts";

/**
 * The rows-side model of one sheet: its row tree, metadata and cell reads,
 * and the persisting verbs a `SheetModel` exposes.
 */
export class Sheet implements ModelSheet {
  static readonly ACTOR = new Actor("Sheet");

  static {
    installSheetFriend({
      stores: (sheet) => Sheet.#own(sheet).#stores,
      internals: (sheet) => Sheet.#own(sheet).#friendInternals(),
      wiring: (sheet) => Sheet.#own(sheet).#friendWiring(),
    });
  }

  /**
   * The `Sheet` behind a friend door's `ModelSheet`: the doors take the
   * interface the layers below name, and answer only for a real sheet (a
   * stand-in has no stores, internals or wiring to hand out).
   */
  static #own(sheet: ModelSheet): Sheet {
    if (!(#stores in sheet)) {
      throw new Error("sheet friend: not a Sheet (only a Sheet has stores, internals and wiring)");
    }
    return sheet;
  }

  /**
   * The sheet's id as the server and every route spell it (`SheetNetworkKey`:
   * the server's UUID for a stored sheet, the spec's spelling — often
   * `ss_<hex>` — for an ephemeral one, any text for a fixture). A string, so
   * it crosses JSON (a snapshot, a tool's answer) as is.
   */
  readonly networkKey: SheetNetworkKey;
  /**
   * `id` parsed as a `SheetId`, or `null` when `id` is not one (a fixture
   * sheet named by other text). Kept beside `id` because it is what the
   * sheet's structure events are scoped by (`NewRowInserted.concerns`,
   * `SheetStructureChanged`): an interned `SheetId` compares a UUID and its
   * `ss_<hex>` spelling as one sheet, which the route text does not.
   */
  readonly id: Option<SheetId>;
  readonly backend: Backend;
  /**
   * The pause between a cell response and its install (`IngestionGate`):
   * `OPEN` by default; the sheet UI holds it while the user is scrolling so
   * a response that lands mid-gesture is installed at release instead of
   * inside a motion frame. Read by `performPrefetch` and the rows' own
   * `_performFetch`; set once per bind through the SDK's `sheetWiring` door.
   */
  readonly #ingestion = new IngestionGate.Slot();
  /** Model reads stop before disposal can wake a deferred install. */
  #retired = false;
  /** Optional response-install admission budget, owned by the renderer host. */
  #installPacer: Option<InstallPacer> = null;
  /** Injected sync-status poll capability (see `SyncPoll`). Null in
   *  non-HTTP / test contexts, in which case batch background-sync
   *  polling is skipped. */
  readonly syncPoll: Option<SyncPoll>;
  /**
   * Coalesces BACKEND cell persists per `(rowKey, date)` so live (per-keystroke)
   * cell commits stay local-immediate (store + formula recompute) while the
   * network write is trailing-debounced; a "now" write (blur / Enter) flushes it.
   * Package-internal (`sheetInternals`).
   */
  readonly #cellWriteCoalescer: CellWriteCoalescer;
  readonly #cellWrites: CellWrites;
  /**
   * Internal — read through the named methods below; see `SheetStores`.
   * A `#` field: the package reaches it only through the `sheetStores` door
   * installed by the static block above.
   */
  readonly #stores: SheetStores;
  readonly registry: RowLocalId.Registry;
  readonly invalidationSink: InvalidationSink;
  /**
   * Client-scoped cell-data store for sheet-invariant canonical
   * rows — provider-backed dataSources reached via `:~`-prefixed
   * lookup keys. Multiple `Sheet` instances under the same Client
   * share this store so they share cells, dedup fetches, and see
   * the same wire-stream pushes.
   *
   * For standalone / test usage, a fresh per-sheet store is used
   * by default; for Client-managed usage, the Client passes its
   * shared store through the constructor: provider-backed rows are
   * sheet-invariant, so sheets share one store for them
   * (`.docs/content/cell-stores-and-handles.md`).
   *
   * Private: read through `peekCanonicalCell` / `subscribeCanonicalCells`; the
   * package writes it through `sheetInternals`.
   */
  readonly #canonicalCells: SourceRow.CellDataStore<string>;

  /**
   * Per-sheet alias-template overlay. Populated by
   * `BuiltinAliasFactory[]` injected through `SheetModelCache`.
   * `expandAliasInstance` and friends consult this map first before
   * falling back to the sheet's `registries.aliasTemplates`. Private: read
   * through `aliasTemplateOverlay`.
   */
  readonly #aliasTemplates: Map<string, AliasTemplate> = new Map();

  /** Per-factory cleanup hooks returned from `factory.onAttach(sheet)`. */
  #builtinAliasCleanups: (() => void)[] = [];
  /**
   * Unsubscribe handles for the sheet's external subscriptions — the
   * shared `canonicalCells` fan-out and the invalidation-sink listener.
   * Run by `dispose()` when a cached instance is discarded (e.g.
   * `SheetModelCache.rekeyRows` replacing an instance), so a forgotten
   * sheet stops reacting to shared-store changes.
   */
  readonly #disposers: (() => void)[] = [];
  /** The formula engine's incremental-recompute coverage (`sheetInternals`). */
  readonly #formulaCoverage: FormulaCoverage = new FormulaCoverage();
  /**
   * Per-sheet registries for shape-driven dataSource interop.
   * Populated by consumers (or the future `registerBuiltins`)
   * before the first row lands; consulted by the normalizing
   * backend (`encodings`) + facade attach (`shapes`, `facades`).
   */
  readonly shapes: ShapeRegistry = new ShapeRegistry();
  readonly encodings: EncodingRegistry = new EncodingRegistry();
  readonly facades: FacadeRegistry = new FacadeRegistry();
  /**
   * The lookup tables this sheet resolves its rows through — row-type specs,
   * computed sources, alias templates, data presets, the render-skip
   * override. Its client's (`ClientConfig.registries`), or the built-ins
   * (`SheetRegistries.BUILTINS`) for a standalone sheet.
   */
  readonly registries: SheetRegistries;

  /**
   * Formula dependency graph, keyed by `RowLocalId`. Rebuilt by
   * `RawSheet.hydrate` after rows land; subsequent formula /
   * label / lookup-key edits maintain edges incrementally via the
   * formula engine (through `sheetInternals`).
   */
  readonly #dependencyGraph = new DependencyGraph<RowLocalId>();
  /** Instrumentation observers (see `SheetWiring`). */
  readonly #recomputeSweepObservers = new Set<RecomputeSweepObserver>();
  readonly #listenerDrainObservers = new Set<ListenerDrainObserver>();
  /** The friend views, built once (`sheetInternals` / `sheetWiring`). */
  #internalsView: Option<SheetInternals> = null;
  #wiringView: Option<SheetWiring> = null;

  /**
   * The formula dependency graph, read-only: its frozen read face
   * (`DependencyGraph.readsOf`) — the formula engine owns the edges and
   * reaches the live graph through `sheetInternals(sheet).graph`.
   */
  get graph(): DependencyGraphReads<RowLocalId> {
    return DependencyGraph.readsOf(this.#dependencyGraph);
  }

  #sheetTitle: Option<string>;
  #sheetWidth: Option<number>;
  #sheetTimescale: Timescale;
  readonly #sheetAccessRole: "owner" | "editor" | "viewer";
  /**
   * `true` when this Sheet was opened ephemerally — the sheet UUID was
   * minted client-side and has no row in the server's `spreadsheets`
   * table. `Sheet.performPrefetch` (the single batch engine — aggregate
   * `ensureLoaded` delegates here) reads this to skip the sheet-scoped batch
   * endpoint (which 404s for unknown sheets) and gate against the owned
   * `:calendar` key instead, which ACL-checks each row.
   *
   * Flipped false in place by `markPromoted()` on an ephemeral→server promote
   * (the sheet now exists server-side), so the promote needs no re-hydrate.
   */
  #isEphemeral: boolean;
  #rowList: ModelRow[];
  readonly #rowsById: Map<RowLocalId, ModelRow>;
  /**
   * The writes of this sheet's rows' own fields (`SheetRowFields`): the
   * row-field ledger they enter (the client's, else this sheet's own over its
   * own copy), this sheet's copy in it, and the display-attribute writes.
   */
  readonly #fields: SheetRowFields;
  /** An alias's fork and reset (`SheetAliasFork`) over this sheet's rows, expansion and row-field writes. */
  readonly #aliasFork: SheetAliasFork;
  /** The header and footer surfaces (`SheetSurfaces`): system rows, surface anchors, server-backed surface rows. */
  readonly #surfaces: SheetSurfaces;

  /** Default-renderer copy-on-write (`SheetRendererCow`) over this sheet's rows, stores and ledger. */
  readonly #rendererCow: SheetRendererCow = new SheetRendererCow({
    backend: { addRow: (...args) => this.backend.addRow(...args), deleteRow: (...args) => this.backend.deleteRow(...args) },
    sheetId: () => this.id,
    addRow: (spec) => this.addRow(spec),
    synthesizeRenderer: (source, shape) => hydrateDefaultRenderer(source, this, shape),
    stores: () => this.#stores,
    rowWrites: () => this.#fields.ledger,
    isEphemeral: () => this.#isEphemeral,
    row: (id) => this.#rowsById.get(id) ?? null,
    has: (id) => this.#rowsById.has(id),
    requireRow: (id, step) => this.#fields.requireRow(id, step),
    dropRowSubtree: (id) => this.#dropRowSubtree(id),
    adoptChild: (child, parentId) => {
      this.#rowList.push(child);
      this.#rowsById.set(child.id, child);
      this.#rowsChanged();
      this.#dependencyGraph.addNode(child.id);
      this.#stores.tree.insert(child.id, parentId, 0);
    },
    snapshot: (row) => snapshotRow(this.#rowSnapshotSource(), row),
    writeRowFields: (rowId, change, context) => this.#fields.write(rowId, change, context),
    beginWireAttributes: (rowId) => this.#fields.beginWireAttributes(rowId),
    rowTransport: (rowId) => this.#fields.transport(rowId),
    writeOpts: () => SheetRowFields.writeOpts(),
  });

  /**
   * The secondary row indices (`SheetRowIndex`): by label, lookup key,
   * formula name and class, and the header / footer surface anchors —
   * rebuilt lazily once a change marks them dirty (`invalidate`).
   */
  readonly #index = new SheetRowIndex<ModelRow>({
    rows: () => this.#rowsById.values(),
    row: (id) => this.#rowsById.get(id) ?? null,
    roots: () => this.#stores.tree.roots(),
    meta: (id) => this.#stores.rowMetadata.get(id),
    formulaInput: (row, meta) => {
      const source = cellBearingRow(row) ?? row;
      return {
        row: source,
        label: meta.label,
        lookupKey: meta.lookupKey,
        source: row instanceof SourceRow,
        identity: source instanceof SourceRow && !source.cellsDerived && source.lookupKey != null && isCanonicalKey(source.lookupKey)
          ? source.lookupKey : String(source.id),
        locator: String(source.id),
      };
    },
    anchor: (row) => surfaceAnchorOf(row),
    dataOwner: (row) => row instanceof SourceRow ? row.dataOwningRowId() : null,
  });
  /**
   * The cells this sheet persists after an ephemeral→server promote
   * (`SheetPromotionFlush`): tracked while the sheet is ephemeral, flushed
   * once the promote landed. Built in the constructor (it reads whether the
   * sheet was born ephemeral).
   */
  readonly #promotionFlush: SheetPromotionFlush<ModelRow>;
  /**
   * The coalesced recompute of this sheet's computed rows
   * (`SheetRecomputeQueue`): parents of changed children and cross-row
   * dependents, each once per outermost dispatch over the union of its ranges.
   */
  readonly #recomputes: SheetRecomputeQueue<ComputedDataSource>;
  #newRecomputeQueue(scheduler: RecomputeScheduler): SheetRecomputeQueue<ComputedDataSource> {
    return new SheetRecomputeQueue<ComputedDataSource>({
      computed: (id) => {
        const row = this.peekRow(id);
        return row instanceof ComputedDataSource ? row : null;
      },
      parent: (id) => this.#stores.tree.parent(id),
      dependents: (id) => DependencyGraph.dependentsOnly(this.#dependencyGraph, id),
      carried: () => this.#prefetch.carriedRows(),
      install: (ids) => this.#prefetch.finishInstalls(ids),
      recompute: (row, range) => this.#recomputeComputedRow(row, range),
    }, scheduler);
  }
  // Deferred-listener mode for bulk-write paths (`installBatchedCells`, `ListDataSource.recompute`). While it is on, the
  // sheet's own `ColumnDataChanged` handler enqueues work (parent recompute + alias re-expansion) but does NOT drain;
  // the outermost `withDeferredListeners` flushes everything once at end — a single drain instead of one per cell write.
  #deferListenerSideEffects = false;
  readonly #pendingAliasReexpansionRowIds = new Set<RowLocalId>();
  /**
   * The re-expansion admission of this sheet's attached aliases
   * (`SheetAliasReexpansion`): when a re-expansion drops an alias's subtree,
   * and which completed expansions certify their signatures.
   */
  readonly #reexpansion = new SheetAliasReexpansion<AliasRow, AliasLocalState>({
    localState: (alias) => AliasTemplateOps.localState(alias),
    prepare: (alias, state) => AliasTemplateOps.prepareExpansion(alias, this, state),
    signature: (alias, state) => AliasTemplateOps.prepareExpansion(alias, this, state, false)?.inputSignature ?? null,
    expandInstance: (alias, state) => AliasTemplateOps.expandInstance(alias, this, state),
    template: (alias) => alias.descriptor.template,
    hydrated: (alias) => alias.instance().hydrated,
    isLive: (alias) => this.#rowsById.get(alias.id) === alias,
    children: (id) => this.#stores.tree.children(id),
    dropSubtree: (id) => this.#dropRowSubtree(id),
    forgetLoadCoverage: () => this.#loads.forgetCoverage(),
    isBatching: () => this.#stores.tree.isBatching,
    recomputeDescendants: (alias) =>
      this.#recomputeComputedDescendants(alias, () => FormulaEngine.observedRange(this)),
  });
  /**
   * When this sheet asks the server for cells (`SheetCellLoads`): the
   * prefetch's coalescing and coverage, the paced batch, the aggregate
   * gather and the sync-key refetch. The install of what a batch answers is
   * the sheet's prefetch unit's (`SheetPrefetch`).
   */
  readonly #loads: SheetCellLoads<SourceRow, BatchRowCells, SourceRow.WriteRevisions> = new SheetCellLoads({
    fetchCellsBatch: (sheetKey, rowKeys, range) => this.backend.fetchCellsBatch(sheetKey, rowKeys, range),
    throttled: (err) => (err instanceof BatchFetchThrottledError ? err : null),
    syncWatcher: () => (this.syncPoll == null ? null : SyncKeyWatcher.for(this.syncPoll)),
    prefetch: (range, only) => this.#prefetch.run(range, only),
    captureWriteRevisions: () => this.#captureWriteRevisions(),
    releaseWriteRevisions: (since) => this.#releaseWriteRevisions(since),
    installOk: (row, range, entry, since, syncKeys) => this.#prefetch.installOk(row, range, entry, since, syncKeys),
  });
  /** What a batch read installs (`SheetPrefetch`): the prefetch's plan, batch, install and fallback. */
  readonly #prefetch: SheetPrefetch = new SheetPrefetch(
    {
      rows: () => this.#rowsById.values(),
      networkKey: () => this.networkKey,
      isEphemeral: () => this.#isEphemeral,
      isRetired: () => this.#retired,
    },
    {
      whenIngestionReleased: () => this.#ingestion.whenReleased().promise,
      admit: () => (this.#installPacer == null ? null : this.#installPacer.admit(this)),
      withDeferredListeners: (fn) => this.#withDeferredListeners(fn),
      captureWriteRevisions: () => this.#captureWriteRevisions(),
      releaseWriteRevisions: (since) => this.#releaseWriteRevisions(since),
      recomputeFormulas: (range) => FormulaEngine.recomputeFormulas(this, range),
      budget: () => this.#budget,
      readers: (id) => this.#recomputes.readers(id),
    },
    this.#loads,
  );
  /** The frame-budget port the recompute queue and the batch installs share (immediate by default). */
  #budget: RecomputeScheduler = SheetRecomputeQueue.IMMEDIATE;

  private constructor(opts: {
    id: SheetNetworkKey;
    title?: string;
    width?: number;
    /** The sheet's timescale; `null` opens at `"day"`. */
    timescale: Option<Timescale>;
    accessRole?: "owner" | "editor" | "viewer";
    backend: Backend;
    rows: readonly ModelRow[];
    stores: SheetStores;
    registry: RowLocalId.Registry;
    invalidationSink: InvalidationSink;
    canonicalCells?: SourceRow.CellDataStore<string>;
    ephemeral?: boolean;
    syncPoll?: SyncPoll;
    registries: SheetRegistries;
    rowWrites: Option<RowWrites>;
    recomputeScheduler?: RecomputeScheduler;
  }) {
    this.#budget = opts.recomputeScheduler ?? SheetRecomputeQueue.IMMEDIATE;
    this.#recomputes = this.#newRecomputeQueue(this.#budget);
    this.networkKey = opts.id;
    this.id = SheetId.parse(opts.id);
    this.#sheetTitle = opts.title ?? null;
    this.#sheetWidth = opts.width ?? null;
    this.#sheetTimescale = opts.timescale ?? "day";
    this.#sheetAccessRole = opts.accessRole ?? "owner";
    this.#isEphemeral = opts.ephemeral ?? false;
    this.#promotionFlush = new SheetPromotionFlush<ModelRow>({
      ephemeral: () => this.#isEphemeral,
      canonicalKeyOf: (id) => {
        const lookupKey = this.#stores.rowMetadata.get(id).lookupKey;
        return isCanonicalKey(lookupKey) ? lookupKey : null;
      },
      canonicalCell: (key, date, cellId) => this.#canonicalCells.get(key, date, cellId),
      localCell: (id, date, cellId) => this.#stores.sourceCellData.get(id, date, cellId),
      canonicalCells: (key) => this.#canonicalCells.cells(key, null),
      localCells: (id) => this.#stores.sourceCellData.cells(id, null),
      rows: () => this.allRows(),
      writable: (row) => row instanceof SourceRow && !(row instanceof FormulaDataSource) && row.cellsWritable,
      writeCell: (rowId, entry) => this.backend.writeCell(rowId, entry, null, null),
    }, this.#isEphemeral);
    this.backend = opts.backend;
    this.syncPoll = opts.syncPoll ?? null;
    this.#stores = opts.stores;
    this.registry = opts.registry;
    this.registries = opts.registries;
    this.invalidationSink = opts.invalidationSink;
    this.#fields = new SheetRowFields(
      { row: (id) => this.#rowsById.get(id) ?? null, networkKey: () => String(this.networkKey) },
      {
        stores: () => this.#stores,
        snapshot: (row) => snapshotRow(this.#rowSnapshotSource(), row),
        formulaChanged: (rowId, row) => {
          FormulaEngine.rebindFormulaDataSource(this, rowId);
          if (row instanceof FormulaDataSource) {
            // L3 (dark): the formula's edges re-declared onto the ledger after the rebind.
            this.#stores.sourceCellData.ledgerShadow?.declareFromGraph(this.#dependencyGraph, row);
          }
          const range = FormulaEngine.observedRange(this);
          if (range != null) FormulaEngine.recomputeRowAndDependents(this, rowId, range);
        },
      },
      {
        isEphemeral: () => this.#isEphemeral,
        transport: (rowId) => (body) => this.backend.updateRow(RowId.fromLocalId(rowId), body, null),
        materializeAndPatchRenderer: (sourceId, patch) => this.#rendererCow.materializeAndPatchRenderer(sourceId, patch),
      },
      opts.rowWrites ?? null,
      this.invalidationSink,
    );
    this.#aliasFork = new SheetAliasFork(
      {
        row: (id) => this.#rowsById.get(id) ?? null,
        stores: () => this.#stores,
        descendants: (id) => collectDescendants(this.#stores.tree, id),
        snapshot: (row) => snapshotRow(this.#rowSnapshotSource(), row),
        addRow: (spec) => this.addRow(spec),
        dropRowSubtree: (id) => this.#dropRowSubtree(id),
      },
      {
        expand: (alias) => AliasTemplateOps.expandInstance(alias, this),
        declaresChildren: (alias) => AliasTemplateOps.declaresChildren(alias, this),
        rebuild: (alias) => {
          this.#reexpansion.supersede(alias.id);
          this.#stores.aliasInstance.reset(alias.id);
          // Force a rebuild: the children are still the real forked rows and the
          // expansion signature (unchanged inputs) would otherwise let the
          // re-expansion skip and leave them in place.
          this.#reexpansion.forgetSignatures(alias.id);
          this.#reexpandAttachedAlias(alias, {
            // The fork state is exactly a hidden input: the children are real forked
            // rows and the (props, timeScale, attributes) signature is unchanged, so
            // no signature can see that they must go.
            kind: "hidden-input",
            path: "alias-fork/reset",
          });
        },
      },
      { backend: this.backend, sheetId: () => this.id, fields: this.#fields },
    );
    this.#surfaces = new SheetSurfaces(
      {
        rows: () => this.#rowList,
        row: (id) => this.#rowsById.get(id) ?? null,
        tree: () => this.#stores.tree,
        resolveRawId: (rawId) => this.registry.resolveFromRawId(rawId),
        surfaceRow: (kind) => (kind === "header" ? this.peekHeaderRow() : this.peekFooterRow()),
      },
      {
        addRow: (spec) => this.addRow(spec),
        dropRowSubtree: (id) => this.#dropRowSubtree(id),
        withDeferredStructure: (fn) => this.#withDeferredStructure(fn),
      },
      { backend: this.backend, sheetId: () => this.id },
    );
    this.#canonicalCells = opts.canonicalCells ?? new SourceRow.CellDataStore<string>();
    const writes = CellWrites.forStore(this.#canonicalCells);
    this.#cellWrites = writes;
    this.#cellWriteCoalescer = writes.coalescer;
    this.#rowList = [...opts.rows];
    this.#rowsById = new Map();
    for (const row of opts.rows) this.#rowsById.set(row.id, row);

    // Any metadata change that touches label / lookupKey invalidates
    // the secondary indices. Formula changes don't affect these maps
    // but do trigger graph rebinds — handled by the formula engine.
    //
    // Label renames additionally rewrite dependent formula text:
    // `rows["prev"]` → `rows["next"]` in every depender, then rebind +
    // recompute. Handled here (not at the variant) so one handler
    // covers every rename path (user setLabel, backend stream, etc.).
    this.#disposers.push(() => this.#formulaCoverage.dispose());
    this.#disposers.push(this.invalidationSink.subscribe((e) => {
      if (e instanceof RowLegendChanged) {
        this.#index.invalidate();
        FormulaEngine.propagateLabelRename(this, e);
        return;
      }
      if (e instanceof RowDataChanged && e.field === "lookupKey") {
        this.#index.invalidate();
        return;
      }
      // A first coverage that carried no cells (`RowRangeCovered`) moves no
      // cell; like a ranged change below, it re-expands an unhydrated alias
      // only when the alias's inputs moved since its last expansion (the
      // provider rows' first load is where a host input is polled).
      if (e instanceof RowRangeCovered) {
        const row = this.peekRow(e.rowId);
        if (row instanceof AliasRow && !row.instance().hydrated && this.#reexpansion.inputMoved(row)) {
          this.#reexpandAttachedAlias(row);
        }
        return;
      }
      // A ranged change (`RowRangeDataChanged`: a fetch installed those
      // dates) announces cell values only, as the formula engine reads it
      // (`cellValueOnly`): the computed cells it affects recompute through
      // the store's `ColumnDataChanged`. It re-expands the alias only when
      // the alias's inputs moved since its last expansion (a provider input
      // such as the host's calendar list is observed here first); an
      // unconditional re-expansion ran a whole-alias recompute per dated
      // arrival.
      if (
        e instanceof RowDataChanged &&
        (e.field == null || e.field === "hydrated" ||
          e.field.startsWith("props.") ||
          e.field.startsWith("overrides."))
      ) {
        const row = this.peekRow(e.rowId);
        if (
          row instanceof AliasRow && !row.instance().hydrated &&
          (!(e instanceof RowRangeDataChanged) || this.#reexpansion.inputMoved(row))
        ) {
          this.#reexpandAttachedAlias(row);
        }
        return;
      }
      // Sync-logs observability: toggling a source row's `showSyncLogs`
      // attribute adds / removes its nested `logs` child (generic across
      // every source row — see `reconcileSyncLogsChild`).
      if (e instanceof RowDataChanged && e.field === "showSyncLogs") {
        const row = this.peekRow(e.rowId);
        if (row instanceof SourceRow) this.#reconcileSyncLogsChild(row);
        return;
      }
      if (e instanceof ColumnDataChanged && e.rowId != null) {
        this.#recomputes.enqueueParent(e.rowId, e.column.toRange());
        this.#recomputes.enqueueDependents(e.rowId, e.column.toRange());
        if (this.#deferListenerSideEffects) {
          this.#pendingAliasReexpansionRowIds.add(e.rowId);
        } else {
          this.#reexpandAliasesForDynamicSource(e.rowId);
          this.#recomputes.drain();
        }
        return;
      }
      if (e instanceof RowDataChanged && e.field === "meta") {
        this.#reexpandAliasesForDynamicSource(e.rowId);
        return;
      }
      if (e instanceof NewRowInserted) {
        // A row entering this sheet binds the formulas waiting on its name
        // (`FormulaEngine.bindResolvedNames`); inside a batch, the drain below.
        if (e.concerns(this.networkKey) && !this.#stores.tree.isBatching) {
          FormulaEngine.bindResolvedNames(this);
        }
        // Under `batchStructural` the batch's single coalesced
        // `SheetStructureChanged` drain runs `recomputeComputedSources` over
        // final state (an insert arms the drain — see `TreeStructure.insert`),
        // so the per-insert recompute would only duplicate it. Outside a
        // batch, check the parent's kind BEFORE paying for the range: the
        // `observedRange` scan is O(rows × cells) and most inserted rows do
        // not feed a computed parent.
        // Inside a hydrate's tree build (`holdInsertRecomputes`) the hydrate
        // recomputes every computed row once over the finished tree instead.
        if (this.#stores.tree.isBatching || this.#insertRecomputesHeld > 0) return;
        const insertParentId = this.#stores.tree.parent(e.rowId);
        const insertParent =
          insertParentId == null ? null : this.peekRow(insertParentId);
        if (insertParent instanceof ComputedDataSource) {
          this.#recomputeComputedRow(insertParent, FormulaEngine.observedRange(this));
        }
        return;
      }
      if (e instanceof SheetStructureChanged && e.concerns(this.id)) {
        FormulaEngine.bindResolvedNames(this);
        this.#recomputeComputedSources(FormulaEngine.observedRange(this));
      }
    }));

    // Fan out canonical-cell changes: when a `:~`-keyed row's cell
    // changes in the Client-scoped store, every sheet with that
    // lookupKey should emit a local `ColumnDataChanged` so its
    // subscribers (renderers, formula engine, facades) rerender as
    // if the row's own store had changed. The store's `init` path
    // bypasses subscribers on purpose (hydrate seeding) so seed
    // pushes don't trigger spurious events.
    this.#disposers.push(this.#canonicalCells.subscribeAll((change) => {
      // Fan out to EVERY row sharing the key — multiple views of one source
      // (e.g. two weather rows at the same location) must all re-render, not
      // just the first one the index happens to return.
      const rows = this.peekAllByLookupKey(change.key);
      if (rows.length === 0) return;
      // Datetime (custom) cells carry a `dtr` rather than a partial
      // date; invalidate the column its start is stored under so subcolumn
      // renderers (stickers, scheduling) re-read, carrying its instants for
      // a viewer whose day columns differ (`columnDataChangedOf`).
      // Forward the originating actor (not Sheet.ACTOR) so the lona-js
      // mirror bridge could surface it for self-echo suppression. No
      // canonical-row plugin consumes `CellActors` today, but preserving
      // the actor keeps this re-emit correct if one is ever added.
      for (const row of rows) {
        this.invalidationSink.emit(columnDataChangedOf(change, row.id, change.actor ?? Sheet.ACTOR));
      }
    }));
  }

  /**
   * Release the sheet's external subscriptions (the shared
   * `canonicalCells` fan-out and the invalidation-sink listener) plus
   * any builtin-alias factory cleanups. Call before discarding a cached
   * instance — e.g. `SheetModelCache.rekeyRows` forgetting an instance it
   * replaces — so a forgotten sheet stops reacting to shared-store
   * changes (otherwise a promote would leave a ghost subscriber double-
   * processing canonical-cell events). Idempotent.
   */
  dispose(): void {
    if (this.#retired) return;
    this.#retired = true;
    this.#loads.retire();
    const failures: { first: Option<() => never> } = { first: null };
    const release = (operation: () => void): void => {
      try { operation(); }
      catch (error) { if (failures.first == null) failures.first = () => { throw error; }; }
    };
    const pacer = this.#installPacer;
    this.#installPacer = null;
    release(() => this.#ingestion.release());
    if (pacer != null) release(() => pacer.release(this));
    // Flush admitted writes even though new reads already see this model retired.
    release(() => this.flushPendingCellWrites());
    // Finish the installs and recomputes a budgeted scheduler carried, end the sheet-wide
    // reads still out, then retire the queue: no recompute runs on a disposed sheet.
    release(() => this.#prefetch.retire());
    release(() => this.#recomputes.flush());
    this.#recomputes.retire();
    // Cancel only this sheet's sync-completion refetch and sync-key leases.
    this.#loads.releaseSyncWork(release);
    release(() => this.#stores.sourceCellData.dispose());
    const disposers = this.#disposers.splice(0);
    for (const unsubscribe of disposers) release(unsubscribe);
    const cleanups = this.#builtinAliasCleanups;
    this.#builtinAliasCleanups = [];
    for (const cleanup of cleanups) release(cleanup);
    const first = failures.first;
    if (first != null) first();
  }

  /** Push every page-local pending cell persist into the backend queue. */
  flushPendingCellWrites(): void {
    this.#cellWriteCoalescer.flushOwner(this);
  }

  /**
   * Current sheet title: the owning client's sheet list entry's while the
   * list holds the sheet (`SheetWiring.setTitleSource`) — kept as the
   * sheet's own once the list no longer holds it (a deleted sheet keeps the
   * title it showed) — else the sheet's own (mutable via `setTitle`).
   */
  get title(): Option<string> {
    const listed = this.#titleSource?.() ?? null;
    if (listed != null) this.#sheetTitle = listed.title;
    return this.#sheetTitle;
  }

  /** Where `title` reads the client's sheet list entry (`setTitleSource`); `null`: nowhere. */
  #titleSource: Option<() => Option<{ readonly title: Option<string> }>> = null;

  /** Update the sheet title. Emits `SheetMetadataChanged(sheetId)`. */
  async #setTitle(title: Option<string>): Promise<void> {
    if (this.#sheetTitle === title) return;
    this.#sheetTitle = title;
    this.invalidationSink.emit(new SheetMetadataChanged(Sheet.ACTOR, SheetKeyRef.of(this.networkKey)));
  }

  /**
   * Sheet-level display width override (pixels). Undefined → viewport
   * picks its own width. Mutable via `setWidth`.
   */
  get width(): Option<number> {
    return this.#sheetWidth;
  }

  /**
   * Update the sheet width override. Emits `SheetWidthChanged(sheetId)`.
   * No-op re-apply does not emit.
   */
  async #setWidth(width: Option<number>): Promise<void> {
    if (this.#sheetWidth === width) return;
    this.#sheetWidth = width;
    this.invalidationSink.emit(new SheetWidthChanged(Sheet.ACTOR, SheetKeyRef.of(this.networkKey)));
  }

  /**
   * Sheet's active timescale (`"day"` / `"week"` / `"month"` /
   * `"year"`). Surfaced to alias templates via
   * `AliasTemplateContext.timescale`; changing it re-expands every
   * alias whose template reads timescale-dependent state.
   * Defaults to `"day"` when not specified.
   */
  get timescale(): Timescale {
    return this.#sheetTimescale;
  }

  /**
   * Update the sheet's active timescale. Compare every registered alias's
   * derived output with its completed expansion. Function-form children
   * may branch on `ctx.timescale` (e.g. calendar swaps
   * `timeline[column]` ↔ `grid`), and a static-array rule's dynamic
   * tail may also read the sheet's scale.
   *
   * No-op re-apply does not re-hydrate.
   */
  setTimescale(timescale: Timescale): void {
    if (this.#sheetTimescale === timescale) return;
    // Output comparison uses the last completed expansion, rather than
    // certifying whatever a provider currently says before this switch.
    this.#sheetTimescale = timescale;
    // The re-expansion fan-out is a structural storm: every function-form
    // alias drops + rebuilds its subtree, and un-batched that is one
    // `SheetStructureChanged` per dropped child — each fanning out
    // synchronously to an O(rows × cells) `observedRange` scan plus a full
    // `recomputeComputedSources` (K× the work of the one coalesced drain,
    // the measured core of the day↔week switch stall). Same discipline as
    // `replaceSystemRows`: one coalesced structural emit, one recompute; the
    // listener deferral additionally collapses the per-cell cascades the
    // drops/re-inserts fire on this sheet's own handler.
    this.#withDeferredListeners(() =>
      this.#withDeferredStructure(() => {
        for (const name of this.#aliasTemplates.keys()) {
          // "output": drop + rebuild only the aliases whose DERIVED
          // child specs actually change with the timescale (the month
          // grid branch); a week<->day switch leaves a timescale-stable
          // subtree — and its rows' fetch coverage — in place.
          this.#reexpandAliasesByTemplate(name, {
            kind: "signature-check",
            mode: "output",
          });
        }
      }),
    );
  }

  /**
   * Current user's access role for this sheet. Fixed at
   * construction — ACL changes require re-fetching the sheet
   * through the Client. Standalone / test sheets default to
   * `"owner"`.
   */
  get accessRole(): "owner" | "editor" | "viewer" {
    return this.#sheetAccessRole;
  }

  /**
   * `true` when this Sheet was opened ephemerally (client-minted UUID,
   * no server-side record). Affects batch-fetch routing — see
   * `performPrefetch`.
   */
  get ephemeral(): boolean {
    return this.#isEphemeral;
  }

  /**
   * Flip ephemeral→server in place after a promote re-key
   * (`SheetModelCache.rekeyRows`). The rows now carry server ids and the sheet
   * exists server-side, so prefetch must batch against the sheet endpoint
   * (the ephemeral branch fans out per-row). Done in place — no re-hydrate —
   * so the promote stays invisible to the renderer.
   */
  #markPromoted(): void {
    this.#isEphemeral = false;
  }

  /**
   * Copy-on-write promote hook, wired by the owning `SheetHandle` (→
   * `ensurePromoted`). The live edit path (`SourceRow.write`) fires it on
   * the first real (non-empty) edit of an ephemeral sheet so the sheet is
   * claimed to the server before its edits are persisted. `null` for standalone
   * / test sheets with no owning handle. Set through `sheetWiring`, read
   * through `sheetInternals`.
   */
  #promoteHookFn: Option<() => Promise<void>> = null;

  /** The value behind `sheetInternals`; reached from the static block. */
  #friendInternals(): SheetInternals {
    if (this.#internalsView != null) return this.#internalsView;
    const view: SheetInternals = {
      reportRequestFailure: (error, report) => this.#prefetch.reportRequestFailure(error, report),
      isRetired: () => this.#retired,
      canonicalCells: this.#canonicalCells,
      cellWriteCoalescer: this.#cellWriteCoalescer,
      cellWrites: this.#cellWrites,
      graph: this.#dependencyGraph,
      formulaCoverage: this.#formulaCoverage,
      aliasTemplates: this.#aliasTemplates,
      withAliasExpansion: (alias, materialize) => this.#withAliasExpansion(alias, materialize),
      promoteHook: () => this.#promoteHookFn,
      addDisposer: (release) => {
        if (this.#retired) release();
        else this.#disposers.push(release);
      },
      markPromoted: () => this.#markPromoted(),
      trackPromotionFlushCell: (rowId, date, cellId) =>
        this.#promotionFlush.track(rowId, date, cellId),
      ingestionGate: this.#ingestion,
      installPacer: () => this.#installPacer,
      captureWriteRevisions: () => this.#captureWriteRevisions(),
      releaseWriteRevisions: (since) => this.#releaseWriteRevisions(since),
      withDeferredListeners: (fn) => this.#withDeferredListeners(fn),
      holdInsertRecomputes: () => this.#holdInsertRecomputes(),
      envelopesFor: (row) => RowEnvelopes.forRow(row),
      sourceRowCells: {
        canonicalCells: this.#canonicalCells,
        cellWriteCoalescer: this.#cellWriteCoalescer,
        cellWrites: this.#cellWrites,
        sourceCellData: this.#stores.sourceCellData,
        rowMetadata: this.#stores.rowMetadata,
        isRetired: () => this.#retired,
        promoteHook: () => this.#promoteHookFn,
        trackPromotionFlushCell: (rowId, date, cellId) => this.#promotionFlush.track(rowId, date, cellId),
        ingestionGate: this.#ingestion,
        installPacer: () => this.#installPacer,
        captureWriteRevisions: () => this.#captureWriteRevisions(),
        releaseWriteRevisions: (since) => this.#releaseWriteRevisions(since),
        withDeferredListeners: (fn) => this.#withDeferredListeners(fn),
        ensureBatchLoaded: (range, rowKey) => this.#prefetch.gatherRowFetch(range, rowKey),
        finishRecompute: (id) => this.#finishCarried(id),
      },
      setTitle: (title) => this.#setTitle(title),
      setWidth: (width) => this.#setWidth(width),
      rowWrites: this.#fields.ledger,
      peekRowFields: (rowId) => this.#fields.peek(rowId),
      showRowFields: (rowId, delta) => this.#fields.show(rowId, delta),
      rowTransport: (rowId) => this.#fields.transport(rowId),
      // The row-options seam takes the verdict as resolve-or-reject; the
      // `deferred-to-promote` flavor it reads from `Sheet.ephemeral`.
      setDisplayAttr: async (rowId, patch) => {
        await this.#fields.setDisplayAttr(rowId, patch);
      },
      setChildDisplayAttrs: async (rowId, preset, patch) => {
        await this.#fields.setChildDisplayAttrs(rowId, preset, patch);
      },
      watchSyncKeys: (keys, range) => this.#loads.watchSyncKeys(keys, range),
      coveringPrefetch: (range) => this.#loads.coveringPrefetch(range),
      ensureBatchLoaded: (range, rowKey) => this.#prefetch.gatherRowFetch(range, rowKey),
      finishRecompute: (id) => this.#finishCarried(id),
      gatherAggregateBatch: (gate, rows, range) => this.#loads.gatherAggregateBatch(gate, rows, range),
      fetchCellsBatchPaced: (sheetKey, rowKeys, range) =>
        this.#loads.fetchCellsBatchPaced(sheetKey, rowKeys, range),
      reexpandAliasesByTemplate: (name, cause) => this.#reexpandAliasesByTemplate(name, cause),
      resetAlias: (aliasId) => this.#aliasFork.reset(aliasId),
      ensureRendererMaterialized: (sourceId) => this.#rendererCow.ensureRendererMaterialized(sourceId),
      resetRendererToDefault: (sourceId) => this.#rendererCow.resetRendererToDefault(sourceId),
      addToHeaderSurface: (spec) => this.#surfaces.addToSurface("header", spec),
      addToFooterSurface: (spec) => this.#surfaces.addToSurface("footer", spec),
      replaceSystemRows: (rows) => this.#surfaces.replaceSystemRows(rows),
      flushCellsToBackend: () => this.#promotionFlush.flush(),
    };
    this.#internalsView = view;
    return view;
  }

  /** What a row snapshot reads (`snapshotRow`): the stores and the canonical cell store. */
  #rowSnapshotSource(): RowSnapshotSource {
    return {
      rowMetadata: this.#stores.rowMetadata,
      tree: this.#stores.tree,
      aliasInstance: this.#stores.aliasInstance,
      renderless: this.#stores.renderless,
      sourceCellData: this.#stores.sourceCellData,
      canonicalCells: this.#canonicalCells,
    };
  }

  /** The value behind `sheetWiring`; reached from the static block. */
  #friendWiring(): SheetWiring {
    if (this.#wiringView != null) return this.#wiringView;
    const view: SheetWiring = {
      setRequestFailureReporter: (reporter) => this.#prefetch.setRequestFailureReporter(reporter),
      installIngestionGate: (gate) => this.#ingestion.set(gate),
      clearIngestionGate: (owner) => this.#ingestion.clear(owner),
      setInstallPacer: (pacer) => {
        const previous = this.#installPacer;
        if (previous === pacer) return;
        this.#installPacer = pacer;
        if (previous != null) previous.release(this);
      },
      clearInstallPacer: (owner) => {
        if (this.#installPacer !== owner) return;
        owner.release(this);
        this.#installPacer = null;
      },
      ingestionSlot: this.#ingestion,
      setPromoteHook: (hook) => {
        this.#promoteHookFn = hook;
      },
      setTitleSource: (source) => {
        this.#titleSource = source;
      },
      localCells: this.#stores.sourceCellData,
      copyFormulaGraph: (destination) => copyFormulaGraph(this, destination),
      patchAttributesLocal: (rowId, patch) => this.#fields.patchLocal(rowId, patch),
      rowWrites: this.#fields.ledger,
      setParentLocal: (id, parent) => this.#setParentLocal(id, parent),
      observeSourceInstalls: (observer) => observeSourceInstalls(this, observer),
      observeStoreInstalls: (store, observer) =>
        store === "canonical"
          ? this.#canonicalCells.observeInstalls(observer)
          : this.#stores.sourceCellData.observeInstalls(observer),
      observeRecomputeSweeps: (observer) =>
        Sheet.#observe(this.#recomputeSweepObservers, observer),
      observeListenerDrains: (observer) =>
        Sheet.#observe(this.#listenerDrainObservers, observer),
      replaceSystemRows: (rows) => this.#surfaces.replaceSystemRows(rows),
      localCellFeed: () => this.#localCellFeed(),
      beginSheetRead: (range) => this.#prefetch.beginSheetRead(range),
      flushCellsToBackend: () => this.#promotionFlush.flush(),
    };
    this.#wiringView = view;
    return view;
  }

  /** Add `observer` to `set` (one entry per call); returns the unsubscribe. */
  static #observe<F extends (...args: never[]) => void>(
    set: Set<F>,
    observer: F,
  ): () => void {
    set.add(observer);
    return () => {
      set.delete(observer);
    };
  }

  /**
   * Declare a canonical source row's cell cardinality to the client-scoped
   * `canonicalCells` store, which is keyed by lookupKey rather than by row id
   * and so cannot derive it from the row itself.
   *
   * This has to happen at INSERTION, for every canonical source row, whether
   * or not the row arrives carrying inline cells. `RawSheet.hydrate` declared
   * it only from inside its inline-cell seeding loop — behind that loop's
   * `rawCells.length === 0` continue — and `addRow` (the path every hydrated
   * `alias.calendar-list` child takes) did not declare it at all. A
   * per-calendar `:~{source}:{authId}:calendar:{calId}:events` row hits both
   * gaps: it is an alias child AND it arrives with no inline cells, because
   * events are fetched by range afterwards.
   *
   * Undeclared, the first canonical write lazily built the `RowCellData` at
   * the `"single"` default (`ensureStore`). Timed events survived that —
   * timepoint cells route to the id-keyed custom store on any cardinality —
   * but an all-day cell went to the date-keyed standard lane, which has no
   * room for a cell id, so the event id was dropped. The `list` aggregate
   * above these rows IS `multi`, so when `ListDataSource` projected that
   * id-less cell upward `RowCellData.set` threw `multi-cardinality row
   * requires cellId`, out of `EventOps.insertOptimistic` and before the
   * create's POST: the event painted optimistically and was never persisted.
   *
   * Re-declaring the same value is a no-op, which matters because several
   * views legitimately share one canonical key (a calendar's body list and
   * its all-day strip). `setCardinality` only rejects a CHANGE once a store
   * holds data, so skip when a live store already disagrees and leave that
   * store's own guard to surface the drift rather than crashing hydration.
   */
  #declareCanonicalCardinality(
    rawRow: RawRow,
    cardinality: CellCardinality,
    source: ModelRow,
  ): void {
    if (source instanceof SourceRow && source.cellsDerived) return;
    const attrLookupKey = rawRow.attributes?.lookupKey;
    const lookupKey = rawRow.lookupKey ??
      (typeof attrLookupKey === "string" ? attrLookupKey : undefined);
    if (!isCanonicalKey(lookupKey)) return;
    const existing = this.#canonicalCells.rowData(lookupKey);
    if (existing && existing.cardinality !== cardinality) return;
    this.#canonicalCells.setCardinality(lookupKey, cardinality);
  }

  /**
   * Resolve the server-declared cell encoding for `rowId`.
   *
   * Prefers `metadata.encoding` when present (backward compat for
   * rows stamped before the shape-only change). Falls back to
   * `metadata.shape` → encoding lookup (current canonical path).
   * Unset means the row's cells are already canonical.
   */
  encodingForRowId(rowId: RowId): Option<string> {
    const localId = this.registry.resolveFromRawId(rowId.toString());
    const meta = this.#stores.rowMetadata.get(localId).meta;
    const encoding = meta?.encoding;
    if (typeof encoding === "string") return encoding;
    const shape = meta?.shape;
    if (typeof shape === "string") return shapeToEncoding(shape);
    return null;
  }

  /** Resolve the server-declared hydrated cell shape for `rowId`. */
  shapeForRowId(rowId: RowId): Option<string> {
    const localId = this.registry.resolveFromRawId(rowId.toString());
    const shape = this.#stores.rowMetadata.get(localId).meta?.shape;
    return typeof shape === "string" ? shape : null;
  }

  /**
   * Convenience: is the current user allowed to write through
   * this sheet? Derived from `accessRole` — viewers are
   * read-only; owners + editors can write. App-layer
   * controllers (row list, gesture handlers) gate write paths
   * on this.
   */
  get editable(): boolean {
    return this.#sheetAccessRole !== "viewer";
  }

  /**
   * Construct a Sheet, resolving the circular `ModelRow.sheet ↔
   * Sheet.rootRows` reference via a builder callback. The builder sees
   * the (empty) Sheet and returns the typed row instances; this
   * method installs them.
   */
  static withRows(
    opts: {
      id: SheetNetworkKey;
      title?: string;
      width?: number;
      /** The sheet's timescale; `null` opens at `"day"`. */
    timescale: Option<Timescale>;
      accessRole?: "owner" | "editor" | "viewer";
      backend: Backend;
      stores: SheetStores;
      registry: RowLocalId.Registry;
      invalidationSink: InvalidationSink;
      canonicalCells?: SourceRow.CellDataStore<string>;
      builtinAliases?: readonly BuiltinAliasFactory[];
      ephemeral?: boolean;
      syncPoll?: SyncPoll;
      registries: SheetRegistries;
      rowWrites: Option<RowWrites>;
      /** How much of a computed-row recompute runs per tick (default: all of it, now). */
      recomputeScheduler?: RecomputeScheduler;
    },
    buildRows: (sheet: Sheet) => readonly ModelRow[],
    /**
     * Optional hook that runs after the sheet is constructed but
     * before rows are built. Use this to register facade factories
     * (and any other per-sheet config) so the attach loop can see
     * them when rows install.
     */
    onReady: Option<(sheet: Sheet) => void>,
  ): Sheet {
    const sheet = new Sheet({ ...opts, rows: [] });

    // Register `builtinAliases` BEFORE `buildRows` runs so alias
    // template lookups during hydration see the per-sheet overlay.
    for (const factory of opts.builtinAliases ?? []) {
      if (sheet.#aliasTemplates.has(factory.templateName)) {
        throw new Error(
          `Sheet.withRows: duplicate builtin alias factory for ` +
            `template "${factory.templateName}".`,
        );
      }
      sheet.#aliasTemplates.set(factory.templateName, factory.template);
    }

    onReady?.(sheet);
    const rows = buildRows(sheet);
    sheet.#rowList = [...rows];
    for (const row of rows) {
      sheet.#rowsById.set(row.id, row);
      if (row instanceof SourceRow) sheet.#stores.sourceCellData.declareCellOwnership(row.id, row.cellsDerived ? "derived" : "stored");
    }
    sheet.#index.invalidate();
    sheet.#rowsChanged();

    // Attach factory listeners after rows land — `onAttach` may
    // immediately call `sheet.reexpandAliasesByTemplate`.
    for (const factory of opts.builtinAliases ?? []) {
      const cleanup = factory.onAttach?.(sheet);
      if (cleanup) sheet.#builtinAliasCleanups.push(cleanup);
    }

    return sheet;
  }

  /**
   * Add one row to the live sheet. Mirrors the per-row section of
   * `hydrateSheet` so the result is indistinguishable from a
   * row that was present in the original hydrate input.
   *
   * Creates the descriptor, instantiates the typed variant, wires
   * the tree / cardinality / alias-instance / metadata stores, and
   * (if `FormulaDataSource`) rebinds the dependency graph + recomputes.
   * `spec` matches `RawRow` minus the `cells` field — cells arrive
   * later through `setCell` ops so each mutation is a distinct
   * observable step.
   *
   * Returns the new `ModelRow` so callers can chain.
   */
  addRow(spec: AddRowSpec): ModelRow {
    const rawRow: RawRow = { ...spec };
    const id = rowLocalIdOf(rawRow, this.registry);
    const descriptor = descriptorOf(rawRow, this.registries);

    if (this.#rowsById.has(id)) {
      throw new Error(
        `Sheet.addRow: row "${spec.id}" already exists on this sheet`,
      );
    }

    const row = parseRow(id, descriptor, this, rawRow.type);

    // Register the row and seed per-store state BEFORE tree.insert
    // so subscribers that resolve `sheet.peekRow(event.rowId)` during
    // the `NewRowInserted` handler see the row in place. Mirrors
    // hydrate's ordering — hydrate installs rows via `Sheet.withRows`
    // (populates `rowsById`) before any tree.insert emits.
    this.#rowList.push(row);
    this.#rowsById.set(id, row);
    this.#index.invalidate();
    this.#rowsChanged();
    this.#dependencyGraph.addNode(id);

    if (descriptor.role === "source") {
      if (!(row instanceof SourceRow)) throw new Error("Sheet.addRow: source descriptor has no source variant");
      this.#stores.sourceCellData.declareCellOwnership(id, row.cellsDerived ? "derived" : "stored");
      this.#stores.sourceCellData.setCardinality(id, descriptor.cellCardinality);
      this.#declareCanonicalCardinality(rawRow, descriptor.cellCardinality, row);
    }
    if (descriptor.role === "alias") {
      const a = spec.attributes ?? {};
      this.#stores.aliasInstance.init(id, {
        template: descriptor.template,
        props: AliasTemplateOps.resolveInitialProps(descriptor.template, a, rawRow),
        overrides: Json.child(a, "overrides") ?? {},
        hydrated: Boolean(a.hydrated),
      });
    }
    // Merge static RowTypeSpec defaults with the spec's
    // attributes (same path `RawSheet.hydrate` uses). Returns
    // `undefined` when no defaults + no instance attrs, keeping
    // `metadata.attributes` absent for bare rows.
    const reconciledAttrs = RowTypeSpecOps.reconcileForWireType(
      rawRow.type,
      rawRow,
      spec.attributes ?? null,
      this.registries,
    );
    // Mirror hydrate's meta initialization so the new row carries the
    // same reconciled row-type metadata defaults that serialize→rehydrate
    // would produce. The wire `type` itself is derived from the row's
    // descriptor (`wireTypeOf` / `typeForRow`), not stored on meta.
    const meta = reconcileRowMetadata(rawRow, this.registries);
    const metadata = RowMetadata.of({
      ...(spec.label !== undefined ? { label: spec.label } : {}),
      ...(spec.lookupKey !== undefined ? { lookupKey: spec.lookupKey } : {}),
      ...(spec.formula !== undefined ? { formula: spec.formula } : {}),
      ...(spec.heightPx !== undefined ? { heightPx: spec.heightPx } : {}),
      ...(reconciledAttrs ? { attributes: reconciledAttrs } : {}),
      ...(Object.keys(meta).length > 0 ? { meta } : {}),
      ...(spec.graph !== undefined ? { graph: spec.graph } : {}),
    });
    this.#stores.rowMetadata.init(id, metadata);

    // Now emit the tree event. Parent resolution matches hydrate:
    // null parentId → sheet root.
    const parentLocalId = spec.parentId
      ? this.registry.resolveFromRawId(spec.parentId)
      : null;
    const siblingCount = parentLocalId === null
      ? this.#stores.tree.roots().length
      : this.#stores.tree.children(parentLocalId).length;
    this.#stores.tree.insert(id, parentLocalId, siblingCount);

    // FormulaDataSource adds need their edges bound + cells computed.
    if (row instanceof FormulaDataSource) {
      FormulaEngine.rebindFormulaDataSource(this, id);
      const range = FormulaEngine.observedRange(this);
      if (range) FormulaEngine.recomputeRowAndDependents(this, id, range);
    }

    // Auto-hydrate the source's default renderer child, mirroring
    // `RawSheet.hydrate`. Skip when the source already has the
    // deterministic child id installed (e.g. re-adding a row that was
    // serialized with its renderer child), the lookup declines, or the source
    // is render-less (copy-on-write: its renderer was materialized into a real
    // child re-added separately, so synthesizing a virtual one would dup it).
    if (row instanceof SourceRow && !this.#stores.renderless.isRenderless(id)) {
      const shape = shapeFromMeta(this.#stores.rowMetadata.get(id).meta ?? null);
      const child = hydrateDefaultRenderer(row, this, shape);
      if (child != null && !this.#rowsById.has(child.id)) {
        this.#rowList.push(child);
        this.#rowsById.set(child.id, child);
        this.#rowsChanged();
        this.#dependencyGraph.addNode(child.id);
        this.#stores.tree.insert(child.id, id, 0);
      }
    }
    if (row instanceof AliasRow) {
      AliasTemplateOps.expandInstance(row, this);
      this.#recomputeComputedDescendants(row, () => FormulaEngine.observedRange(this));
    }

    this.#recomputeComputedParentForChild(id, FormulaEngine.observedRange(this));
    if (row instanceof ComputedDataSource) {
      this.#recomputeComputedRow(row, FormulaEngine.observedRange(this));
    }

    return row;
  }

  /**
   * Delete a row + every descendant.
   *
   * Emits one `SheetStructureChanged` (from `tree.delete`) followed
   * by `ColumnDataChanged(rowId, date)` per cleared cell. After the
   * clears land, downstream formula rows are recomputed in one
   * batch per deleted subtree entry — so any row whose formula
   * references the deleted source sees fresh (null-upstream)
   * values rather than zombie computed state. `ModelRow.delete`
   * delegates here so it doesn't have to import `formula-engine`
   * (which would cycle back through `variants/*`).
   */
  deleteRow(id: RowLocalId): void {
    const tree = this.#stores.tree;
    const cellData = this.#stores.sourceCellData;
    const subtree = [id, ...collectDescendants(tree, id)];
    const subtreeSet = new Set(subtree);
    // Structural readers must see the pruned fallback when tree.delete notifies.
    // Sources remain resolvable by id/name through the recompute below.
    this.#rowList = this.#rowList.filter((row) => !subtreeSet.has(row.id));
    tree.delete(id);

    // Unregister the deleted FORMULA rows from the by-id registry BEFORE
    // the clear + recompute cascade below. Clearing a reparented source
    // stales its formula edges, and `recomputeDependents` resolves each
    // dependent through `rowsById` and re-evaluates it; a still-registered
    // dead formula row would get fresh zombie cells written back to it
    // (delete-resurrection → ghost columns in the live views). Deleted
    // SOURCE rows are deliberately NOT removed here: a surviving dependent
    // formula must still resolve `rows["<deleted source>"]` mid-recompute
    // to refill with the null-upstream value, and unregistering the source
    // early makes that lookup throw (swallowed in `evaluateRowAcrossRange`),
    // silently dropping the refill. Sources + graph nodes are retired after
    // the recompute (below).
    for (const rid of subtree) {
      if (this.#rowsById.get(rid) instanceof FormulaDataSource) {
        this.#rowsById.delete(rid);
        this.#index.invalidate();
        this.#rowsChanged();
      }
    }

    // Clear cells + collect the cleared dates per row so the
    // cascade recompute runs once per row across the union of its
    // cleared dates (not once per cell).
    const clearedByRow = new Map<RowLocalId, NaiveDate.Partial[]>();
    for (const rid of subtree) {
      for (const cell of cellData.cells(rid, null)) {
        cellData.clear(rid, cell.date, cell.id ?? null, null);
        let list = clearedByRow.get(rid);
        if (!list) {
          list = [];
          clearedByRow.set(rid, list);
        }
        list.push(cell.date);
      }
    }

    for (const [rid, dates] of clearedByRow) {
      const [first] = dates;
      if (first === undefined) continue;
      let min = first.start;
      let max = first.end;
      for (const dt of dates) {
        if (dt.start.dse < min.dse) min = dt.start;
        if (dt.end.dse > max.dse) max = dt.end;
      }
      FormulaEngine.recomputeDependents(this, rid, new NaiveDate.Range(min, max));
    }

    // Finish teardown after the recompute. Drop every remaining deleted
    // row (the sources kept resolvable above) from the by-id registry so
    // `peekByLabel` / `peekByLookupKey` (which iterate `rowsById` to
    // rebuild their indices) don't return zombies, and retire the
    // subtree's dependency-graph nodes (+ their incident edges). Nodes are
    // retired here — AFTER the recompute — because `removeNode` also drops
    // incident edges, so retiring earlier would sever surviving external
    // dependents from the deleted source before they could re-evaluate
    // against its removal.
    for (const rid of subtree) {
      this.#stores.sourceCellData.detach(rid);
      this.#rowsById.delete(rid);
      this.#dependencyGraph.removeNode(rid);
    }
    this.#index.invalidate();
    this.#rowsChanged();
    // Formulas that read a deleted row wait for its name again.
    FormulaEngine.releaseDeletedRows(this, subtree);
  }

  /** Top-level sheet rows (children of the sheet root), in tree order. */
  get rootRows(): readonly ModelRow[] {
    const rootIds = this.#stores.tree.roots();
    if (rootIds.length === 0) return this.#rowList;
    const out: ModelRow[] = [];
    for (const id of rootIds) {
      const row = this.#rowsById.get(id);
      if (row) out.push(row);
    }
    return out;
  }

  // ─── Read / seed surface over the internal stores ─────────────────

  /** The row's reconciled metadata (label, lookupKey, attributes, meta, formula, …). */
  rowMeta(id: RowLocalId): RowMetadata {
    return this.#stores.rowMetadata.get(id);
  }

  /** The row's parent in the sheet tree; `null` for a root (or an unknown row). */
  parentOf(id: RowLocalId): Option<RowLocalId> {
    return this.#stores.tree.parent(id);
  }

  /**
   * Before a read of row `id`: install its batch answer if the frame budget
   * carried it (`SheetPrefetch.finishInstall`), then recompute it if a
   * scheduled tick carries it (`SheetRecomputeQueue.finish`).
   */
  #finishCarried(id: RowLocalId): void {
    this.#prefetch.finishInstall(id);
    this.#recomputes.finish(id);
  }

  /** The row's children in tree order. */
  childrenOf(id: RowLocalId): readonly RowLocalId[] {
    return this.#stores.tree.children(id);
  }

  /** The root rows' ids in tree order. */
  rootIds(): readonly RowLocalId[] {
    return this.#stores.tree.roots();
  }

  /**
   * The row's date-keyed cells, optionally range-filtered. A computed row a
   * scheduled recompute tick carries is finished first, as its own reads do.
   */
  peekCells(
    rowId: RowLocalId,
    range: Option<NaiveDate.Range>,
  ): readonly CellEntry[] {
    this.#finishCarried(rowId);
    return this.#stores.sourceCellData.cells(rowId, range);
  }

  /**
   * One cell's value (`cellId` picks the entry on a multi-cardinality row). A
   * carried computed row is finished first (`peekCells`).
   */
  peekCell(
    rowId: RowLocalId,
    date: NaiveDate.Partial,
    cellId: Option<CellId>,
  ): Option<CellValue> {
    this.#finishCarried(rowId);
    return this.#stores.sourceCellData.get(rowId, date, cellId);
  }

  /**
   * One canonical (`:~` lookup-keyed) cell's value from the client-shared
   * canonical store — the `peekCell` of a provider-backed row, addressed by its
   * lookup key (`cellId` picks the entry on a multi-cardinality key).
   */
  peekCanonicalCell(
    lookupKey: string,
    date: NaiveDate.Partial,
    cellId: Option<CellId>,
  ): Option<CellValue> {
    return this.#canonicalCells.get(lookupKey, date, cellId);
  }

  /**
   * Call `onChange` after every change to `lookupKey`'s canonical cells (a
   * set, a clear, a timepoint create / move / delete) from any sheet sharing
   * the store. Returns the unsubscribe.
   */
  subscribeCanonicalCells(lookupKey: string, onChange: () => void): Unsubscribe {
    return this.#canonicalCells.subscribe(lookupKey, () => onChange());
  }

  /**
   * The sheet-local cell store as a subscriber sees it — change and burst
   * subscriptions plus the install-provenance flag (see `SheetLocalCellFeed`).
   * Canonical rows never write it; their cells are `peekCanonicalCell`'s.
   */
  #localCellFeed(): SheetLocalCellFeed {
    return this.#stores.sourceCellData;
  }

  /** The render-skip ledger's current tau for this sheet's cell store; `0` without a ledger. */
  ledgerTau(): number {
    return this.#stores.sourceCellData.ledgerBumper?.recorder?.tau() ?? 0;
  }

  /**
   * Move `id` under `parent` (`null`: a root) in this sheet's tree, locally —
   * nothing is persisted and sibling order is untouched. The mirror of a move
   * the owning handle has already sent to the server; a no-op when `id` is not
   * in this sheet or already has that parent.
   */
  #setParentLocal(id: RowLocalId, parent: Option<RowLocalId>): void {
    if (this.peekRow(id) == null) return;
    if (this.#stores.tree.parent(id) === parent) return;
    this.#stores.tree.reparent(id, parent, null);
  }

  /** The per-sheet alias-template overlay's entry for `name`; `null` when the sheet has none. */
  aliasTemplateOverlay(name: string): Option<AliasTemplate> {
    return this.#aliasTemplates.get(name) ?? null;
  }

  /**
   * The render-cursor registry of this sheet's cell store (the LR6 render-skip
   * surface a binder mints its cursors from); `null` when the store has no
   * ledger binding.
   */
  renderCursors(): Option<RenderCursors> {
    return this.#stores.sourceCellData.renderCursors;
  }

  /**
   * The scene stamp for `rowId` — the data address plus the `(values, attr)`
   * versions LR6's render skip decides on, read from the same probe, so a
   * scene stamp and a skip decision never disagree. `null` when the row has no
   * versioned availability.
   */
  sceneStamp(rowId: RowLocalId): Option<SceneNodeStamp> {
    const store = this.#stores.sourceCellData;
    const versions = store.currentVersions(rowId);
    if (versions == null) return null;
    return {
      address: store.dataAddress(rowId),
      valuesVersion: versions.values,
      attrVersion: versions.attr,
    };
  }

  /**
   * The row `id` names, `null` when it is not in this sheet. A caller holding
   * a `RowId` or a wire key resolves it first (`registry.resolve(RowKey)` /
   * `registry.resolveFromRawId(raw)`).
   */
  peekRow(id: RowLocalId): Option<ModelRow> {
    return this.#rowsById.get(id) ?? null;
  }

  /** The row facades this sheet hands out (`facadeFor`): one per row, kept for the sheet's lifetime. */
  readonly #facades = new RowFacade.Table();

  /**
   * THE facade of row `localId` (the SDK's `SheetRow.Handle`) — ONE per
   * (Sheet, RowLocalId) for the sheet's lifetime, across joined-tree
   * rebuilds, re-keys and the COW promote (the row keeps its `RowLocalId`):
   * every door onto the row hands out this object, so a renderer may key
   * state by it (`===`). Minted the first time by the sheet's owner
   * (`ownFacades`); throws when no owner holds the sheet.
   */
  facadeFor(localId: RowLocalId): RowFacade {
    return this.#facades.facadeFor(localId);
  }

  /** Row `localId`'s facade if one was minted (`facadeFor`), else `null`. */
  peekFacade(localId: RowLocalId): Option<RowFacade> {
    return this.#facades.peek(localId);
  }

  /**
   * Register `owner` — the SDK's handle over this sheet — as the one that
   * mints this sheet's row facades and answers their calls (the newest
   * registered answers). The answer releases it: the one before answers
   * again; the facades stay.
   */
  ownFacades(owner: RowFacade.Owner): () => void {
    return this.#facades.own(owner);
  }

  /** The owner answering this sheet's row facades now (`ownFacades`), `null` when none holds the sheet. */
  get facadeOwner(): Option<RowFacade.Owner> {
    return this.#facades.owner;
  }

  /**
   * Resolve a formula name using one shape-cached snapshot: lookup keys before labels,
   * cell-bearing sources before structural wrappers. Distinct inputs are ambiguous;
   * duplicate views of one ordinary canonical input resolve to that same input.
   */
  formulaReference(name: string):
    | { readonly kind: "resolved"; readonly row: ModelRow }
    | { readonly kind: "missing" }
    | { readonly kind: "ambiguous"; readonly candidates: readonly ModelRow[] } {
    const candidates = this.#index.formulaCandidates(name);
    const [row] = candidates;
    return row == null ? { kind: "missing" } : candidates.length === 1
      ? { kind: "resolved", row } : { kind: "ambiguous", candidates };
  }

  /** Formula references that currently name multiple distinct inputs, sorted by row id and literal. */
  get formulaAmbiguities(): readonly {
    readonly rowId: RowLocalId;
    readonly name: string;
    readonly candidates: readonly RowLocalId[];
  }[] {
    const out: { rowId: RowLocalId; name: string; candidates: readonly RowLocalId[] }[] = [];
    for (const [name, rows] of this.#dependencyGraph.unresolvedNames) {
      const resolution = this.formulaReference(name);
      if (resolution.kind !== "ambiguous") continue;
      for (const rowId of rows) out.push({ rowId, name, candidates: resolution.candidates.map(row => row.id) });
    }
    return out.sort((a, b) => String(a.rowId).localeCompare(String(b.rowId)) || a.name.localeCompare(b.name));
  }

  /** Look up a row by its `label`. O(1) — indices rebuild lazily on dirty. */
  peekByLabel(label: string): Option<ModelRow> {
    return this.#index.peekByLabel(label);
  }

  /**
   * Look up *a* row by its `lookupKey`. O(1). When several rows share the key
   * (multiple views of one shared source) this returns any one of them — fine
   * for callers that just need a source handle (formula, dynamicTail), since
   * the cells are shared. Use `peekAllByLookupKey` to reach every view.
   */
  peekByLookupKey(key: string): Option<ModelRow> {
    return this.#index.peekByLookupKey(key);
  }

  /**
   * Every row in this sheet carrying `lookupKey`. Multiple rows share a key
   * when they are distinct views of one shared source (cells live once in
   * `canonicalCells`); a canonical-cell change must re-render all of them.
   */
  peekAllByLookupKey(key: string): ModelRow[] {
    return this.#index.peekAllByLookupKey(key);
  }

  /**
   * The rows other than `owner` that show `owner`'s cells (aliases and
   * projections of a source), from an index kept until the row set or a
   * lookup key changes; `owner` need not be a row of this sheet.
   */
  peekDataMirrorsOf(owner: RowLocalId): readonly RowLocalId[] {
    return this.#index.dataMirrorsOf(owner);
  }

  /**
   * `FormulaSheetReads.peekCellsByLabelOrKey` — resolves `(cells "LABEL")` in
   * the cross-package formula library. Lookup-key first, then label
   * (matches `(rows["LABEL"])` resolution).
   */
  peekCellsByLabelOrKey(
    labelOrKey: string,
  ): Option<Iterable<{ readonly date: NaiveDate.Partial; readonly value: CellValue }>> {
    const row = FormulaEngine.resolveReference(this, labelOrKey);
    if (!row) return null;
    return cellBearingRow(row)?.peekCells(null).filter((cell) => cell.kind === "date") ?? null;
  }

  /**
   * Every row whose class (or superclass) matches `ctor`, in insertion order.
   * The rows of each class asked for are indexed until the row set changes,
   * so a repeat call reads that class's rows only, not every row of the
   * sheet (the formula engine asks per load: judge P r31 P2).
   */
  findByType<T extends ModelRow>(ctor: RowCtor<T>): readonly T[] {
    return this.#index.findByType(ctor);
  }

  /**
   * The row set changed (a row added to or removed from `#rowsById`): drop
   * the per-class index, and tell the formula coverage its signatures may
   * have moved (`FormulaCoverage.shapeMoved`).
   */
  #rowsChanged(): void {
    this.#index.rowsChanged();
    this.#formulaCoverage.shapeMoved();
  }

  /** Every row in the sheet, flattened breadth-first from the tree root. */
  allRows(): readonly ModelRow[] {
    const out: ModelRow[] = [];
    const queue: RowLocalId[] = [...this.#stores.tree.roots()];
    for (let id = queue.shift(); id !== undefined; id = queue.shift()) {
      const row = this.#rowsById.get(id);
      if (row) out.push(row);
      for (const child of this.#stores.tree.children(id)) queue.push(child);
    }
    return out;
  }

  /**
   * Returns the unique top-level header surface row, or `null`
   * if none exists. Recognizes every `SurfaceAnchor` shape —
   * server-backed surface-container, authored
   * `surface[header]` region row, and the legacy synthetic
   * placeholder — with the server > authored > legacy
   * tie-break; the losers are suppressed for the same render
   * pass. O(1) once the row index rebuilt (`SheetRowIndex`); cached and
   * invalidated on tree-shape changes (`SheetRowIndex.invalidate`).
   */
  peekHeaderRow(): Option<ModelRow> {
    return this.#index.peekHeaderRow();
  }

  /**
   * Returns the unique top-level footer surface row, or `null`
   * if none exists. Recognizes every `SurfaceAnchor` shape for
   * `"footer"`. Same tie-break + caching as `peekHeaderRow`.
   */
  peekFooterRow(): Option<ModelRow> {
    return this.#index.peekFooterRow();
  }

  /**
   * Make sure a server-backed surface row exists for `kind`.
   * Returns the existing one if present; otherwise issues a
   * `Backend.addRow(..., surface: kind)` write, mirrors the
   * resulting row into the local sheet, and returns it.
   *
   * Public so consumers that need to *reparent* an existing row
   * under the surface (e.g. a host's drag-into-sticky-area delegate
   * in its sheet property drawer) can
   * resolve the surface row before issuing the move. Callers
   * adding *new* rows should prefer `addToHeaderSurface` /
   * `addToFooterSurface` which bundles the create + parent in
   * one call.
   *
   * The local mirror uses the same UUID the client sent — the
   * `/api/rows` POST endpoint accepts the client-picked id, so
   * the local mirror's id matches what gets persisted.
   */
  async ensureServerBackedSurface(
    kind: SurfaceKind,
  ): Promise<ModelRow> {
    return await this.#surfaces.ensureServerBacked(kind);
  }

  /** Re-expand the attached alias `alias` for `cause` (`SheetAliasReexpansion.reexpand`). */
  #reexpandAttachedAlias(
    alias: AliasRow,
    cause: ReexpandCause = REEXPAND_INPUT_SIGNATURE,
  ): void {
    this.#reexpansion.reexpand(alias, cause);
  }

  /** Run `operation` as an expansion of `alias` (`SheetAliasReexpansion.withExpansion`; `sheetInternals`). */
  #withAliasExpansion(
    alias: AliasRow,
    operation: (materialize: (plan: AliasExpansionPlan) => void) => void,
  ): void {
    this.#reexpansion.withExpansion(alias, operation);
  }

  /**
   * Re-expand every attached alias whose template matches `name`.
   * Invoked by `BuiltinAliasFactory.onAttach` listeners when their
   * upstream source changes (e.g. preferences `LiveData` fires).
   *
   * Seam 13 — `cause` says WHY, and defaults to the reason this fan-out exists:
   * a hidden input moved. Left `null`, the sheet names that input from the
   * template's own declared dependency (`AliasTemplate.reexpandOn`, which for
   * `alias.default-weather` IS the preference key), so the breadcrumb attributes
   * the burst even when the caller says nothing. A caller that knows the exact
   * event path it matched should pass it — that is strictly better attribution
   * than the declaration, which names the pattern rather than the firing.
   */
  #reexpandAliasesByTemplate(name: string, cause: Option<ReexpandCause>): void {
    // "hidden-input" (⟹ drop always): this fan-out is triggered by an upstream
    // input change (a preference `LiveData` fired) that neither signature in
    // `reexpandAttachedAlias` can observe, so it must rebuild regardless.
    // `setTimescale` passes a `signature-check` on "output" — the timescale IS
    // visible to the derived specs, so only the aliases whose specs change get
    // dropped.
    const resolved: ReexpandCause = cause ??
      { kind: "hidden-input", path: this.#declaredReexpandPath(name) };
    for (const row of this.#rowsById.values()) {
      if (!(row instanceof AliasRow)) continue;
      if (row.descriptor.template !== name) continue;
      if (row.instance().hydrated) continue;
      this.#reexpandAttachedAlias(row, resolved);
    }
  }

  /**
   * The path a template DECLARES it re-expands on (`AliasTemplate.reexpandOn`,
   * the same list `wireDeclaredReexpand` subscribes) — the best name this sheet
   * can give a hidden input without being told. One pattern is reported as
   * itself; several are joined, because a fan-out cannot say which of them
   * fired. Falls back to the template name when nothing is declared (a
   * hand-written `onAttach`, or a template registered only globally).
   */
  #declaredReexpandPath(name: string): string {
    const declared = this.#aliasTemplates.get(name)?.reexpandOn ?? null;
    if (declared == null || declared.length === 0) return `template:${name}`;
    return declared.join("|");
  }

  /**
   * Sync-logs observability. Ensures a source row carries a nested `logs`
   * child iff its `showSyncLogs` attribute is set — the generic mechanism
   * behind the "Show Sync Logs" row option, reconciled on the `showSyncLogs`
   * change. The logs child mirrors the source's sync activity: `logType` is
   * derived from the source's `:~<provider>:` lookup key (e.g. `:~weather:5`
   * → `weather`) so `ColumnLogsRendererPlugin` picks the right display config, and cells are
   * fetched at `<source-key>:logs`. A source without a provider lookup key
   * (a plain user row) still gets the child, but it renders empty. Add /
   * remove only — the source's renderer child is left untouched. The child
   * carries a deterministic `<source>::synclogs` id so re-reconciling is
   * idempotent.
   */
  #reconcileSyncLogsChild(source: SourceRow): void {
    const showSyncLogs =
      Json.boolean(source.attributes(), "showSyncLogs") === true;
    const logsRawId = `${source.id.toString()}::synclogs`;
    const logsId = this.registry.resolveFromRawId(logsRawId);
    const existing = this.#rowsById.get(logsId) ?? null;

    if (showSyncLogs && existing === null) {
      const sourceKey = source.lookupKey;
      const provider = sourceKey?.match(/^:~([a-z0-9-]+):/i)?.[1] ?? null;
      this.addRow({
        id: RowNetworkKey.fromWire(logsRawId),
        type: "logs",
        label: "Sync Logs",
        parentId: RowNetworkKey.of(source.id),
        ...(sourceKey != null ? { lookupKey: `${sourceKey}:logs` } : {}),
        attributes: {
          ...(provider != null ? { logType: provider } : {}),
          parentRowId: source.id.toString(),
        },
      });
    } else if (!showSyncLogs && existing !== null) {
      this.#dropRowSubtree(logsId);
    }
  }

  /**
   * Fork an alias into an editable group ("detach"). Re-keys the
   * alias's synthetic, template-expanded children into real,
   * server-backed rows, flips the instance to `hydrated: true` so the
   * template no longer drives expansion (`expandAliasInstance`
   * early-returns), and persists the promoted children + the alias's
   * new hydrated state.
   *
   * No-op if the alias is already detached. A nested alias is itself
   * materialized (becomes a real child) but keeps its own
   * `hydrated: false`, so on re-insert it re-expands its own children
   * fresh — we never materialize a nested alias's subtree.
   * Auto-hydrated default renderer children are skipped; `addRow`
   * re-creates them deterministically from their source.
   *
   * Returns the old→new `RowLocalId` remap for the promoted rows
   * (empty if the alias was childless or already detached) — callers
   * mid-structural-edit use it to re-resolve a row whose identity the
   * fork just changed (see `forkOwningAlias`).
   *
   * A rejection means nothing stuck: when a persist fails part-way, the
   * fork is compensated before the promise rejects with that failure — the
   * promoted children the backend already created are deleted there, the
   * promoted rows are dropped locally, the alias is re-attached to its
   * template and re-expanded (its synthetic children come back under their
   * deterministic ids). A compensation step the backend refuses too is
   * logged (`alias-fork/compensate-failed`); the fork's own failure is what
   * rejects.
   *
   * Detail: row-arch-reference/rowkey-architecture.md Part 9
   * (in-sync → detached).
   */
  async detachAlias(aliasId: RowLocalId): Promise<Map<RowLocalId, RowLocalId>> {
    return await this.#aliasFork.detach(aliasId);
  }

  /**
   * Copy-on-write write for a renderer display option (number format,
   * checkmark color, unit, …) keyed by the SOURCE row. Materializes the
   * source's synthetic default renderer into a real row on first call, then
   * writes `attr` to the RENDERER's OWN attribute bag — never the source's —
   * so the customization round-trips through serialize → hydrate. Subsequent
   * calls reuse the materialized renderer (idempotent). When the source has no
   * synthesizable renderer (e.g. obj dtypes) the write falls back to the
   * source's bag, preserving today's behavior for those rows.
   *
   * `setDisplayAttr` (and thus the envelope option write,
   * `RowEnvelopeDrive.writeTarget`, behind the SDK row's
   * `row.envelopes.apply`) routes renderer-attr targets here via the
   * renderer's parent source.
   *
   * Checked: the local patch paints first, then the promise resolves on the
   * backend's verdict. When a write it sends is refused or unanswered it
   * rejects with `RowWriteError` (an `SdkRequestError` carrying the
   * backend's outcome, status and reason) and nothing stays: the attribute
   * is back as it was, a renderer this write materialized is dropped (the
   * source re-synthesizes its default one), and a renderer row the server
   * had already created is deleted again — `details` names the write that
   * failed and what of the write had reached the server (step `"add"`: the
   * renderer's create, nothing reached it; step `"update"`: an attribute
   * write). It rejects with an `SdkRequestError` (`outcome:
   * "unaddressable"`) when `sourceId` is not a row of this sheet. An ephemeral
   * (not-yet-promoted) sheet resolves `{ persisted: "deferred-to-promote" }`:
   * nothing is sent, and the value round-trips through serialize → hydrate
   * when the sheet is promoted.
   */
  async setRendererDisplayAttr(
    sourceId: RowLocalId,
    attr: string,
    value: string | number | boolean | null,
  ): Promise<Sheet.AttrWrite> {
    return await this.#rendererCow.materializeAndPatchRenderer(sourceId, { [attr]: value });
  }

  /**
   * Peek the current value of a renderer display option (a synchronous read
   * of the loaded row metadata), dual-reading the
   * renderer child's OWN bag first (where a customization lives once
   * materialized) and falling back to the source's bag (where un-customized
   * options live, including while the default renderer is still virtual). The
   * read counterpart of `setRendererDisplayAttr`; a host's per-plugin option
   * editors initialize their control from this so a reopened editor shows the
   * customized value, not the source's stale default. Returns `null` when the
   * value is absent or non-scalar.
   */
  peekRendererDisplayAttr(
    sourceId: RowLocalId,
    attr: string,
  ): Option<string | number | boolean> {
    return this.#rendererCow.peekRendererDisplayAttr(sourceId, attr);
  }

  /**
   * The id of the attached alias a structural edit on `rowId` should fork,
   * or `null` if none — `rowId` is a templated alias's synthetic child,
   * or is itself an attached alias targeted as a drop parent. Pure /
   * synchronous so a local edit (`RowLocalEdits`) can keep a sync
   * fast-path (no fork → mutate the tree without awaiting). An id, not the
   * row: the fork is `detachAlias(id)` (the handle's, for an app). See
   * rowkey-architecture.md Part 9 (in-sync → detached on structural
   * edit).
   */
  aliasToFork(rowId: RowLocalId): Option<RowLocalId> {
    const row = this.#rowsById.get(rowId);
    if (row === undefined) return null;
    if (row instanceof AliasRow && !row.instance().hydrated) return row.id;
    return AliasTemplateOps.owningAttachedAlias(row)?.id ?? null;
  }

  /**
   * Human-readable display name for an alias `template`, resolved
   * against this sheet's template overlay + the global registry.
   * Surfaced via `AliasRow.displayName`; routed through the sheet so
   * the variant doesn't import `alias-templates` (circular).
   */
  aliasDisplayName(template: string): string {
    return AliasTemplateOps.displayName(template, this);
  }

  #reexpandAliasesForDynamicSource(rowId: RowLocalId): void {
    const source = this.peekRow(rowId);
    const lookupKey = source
      ? this.#stores.rowMetadata.get(source.id).lookupKey
      : undefined;
    if (!lookupKey) return;

    for (const row of this.#rowsById.values()) {
      if (!(row instanceof AliasRow) || row.instance().hydrated) continue;
      if (AliasTemplateOps.resolveDynamicTailSourceKey(row) !== lookupKey) continue;
      this.#reexpandAttachedAlias(row);
    }
  }

  #dropRowSubtree(id: RowLocalId): void {
    const subtree = [id, ...collectDescendants(this.#stores.tree, id)];
    const subtreeSet = new Set(subtree);
    this.#stores.tree.delete(id);
    for (const rid of subtree) {
      this.#dependencyGraph.removeNode(rid);
      this.#stores.sourceCellData.detach(rid);
      this.#rowsById.delete(rid);
      // Nested aliases inside the dropped subtree would otherwise leak their
      // cached expansion signatures forever (the map is keyed by RowLocalId,
      // which is interned per raw id).
      this.#reexpansion.supersede(rid);
      this.#reexpansion.forgetSignatures(rid);
    }
    this.#rowList = this.#rowList.filter((row) => !subtreeSet.has(row.id));
    this.#index.invalidate();
    this.#rowsChanged();
  }

  #recomputeComputedParentForChild(
    childId: RowLocalId,
    range: Option<NaiveDate.Range>,
  ): void {
    const parentId = this.#stores.tree.parent(childId);
    if (parentId == null) return;
    const parent = this.peekRow(parentId);
    if (!(parent instanceof ComputedDataSource)) return;
    this.#recomputeComputedRow(parent, range);
  }

  /**
   * Sheet-wide fetch coordinator — issues `fetchCellsBatch` calls
   * covering every `ListDataSource` constituent in the sheet, then
   * marks each aggregate's batch fetcher so per-plugin /
   * per-aggregate `ensureLoaded` calls in the same range short-circuit.
   *
   * Coalescing + tail-only fetching are the sheet's cell loads'
   * (`SheetCellLoads`, a `RangeBasedFetcher`): concurrent calls for an
   * in-flight range await the same promise, and scrolls extending past the
   * cached effective range issue only the missing slice (with the fetcher's
   * default 28-day padding).
   */
  async ensureLoaded(range: NaiveDate.Range): Promise<void> {
    await this.#loads.ensureLoaded(range);
  }

  /**
   * Bulk-write paths (`SourceRow._installBatchedCells`,
   * `ListDataSource.recompute`) wrap their inner write loop in this
   * helper to suppress per-cell listener side-effects. Each `set` /
   * `clear` still emits its `ColumnDataChanged` to the sink (other
   * subscribers see them) — only this sheet's own handler delays its
   * parent-recompute drain and alias re-expansion until the outermost
   * call returns. Re-entrant calls are no-ops; the outermost flush
   * collapses N redundant cascades into 1. Reached through `sheetInternals`.
   */
  #withDeferredListeners<T>(fn: () => T): T {
    if (this.#deferListenerSideEffects) return fn();
    this.#deferListenerSideEffects = true;
    try {
      return fn();
    } finally {
      this.#deferListenerSideEffects = false;
      this.#drainDeferredListenerWork();
    }
  }

  /**
   * The local-write revisions an install compares against, captured when its
   * request goes out so the flight and the gate wait are both covered (see
   * `CellDataStore`); registered on both stores until released.
   */
  #captureWriteRevisions(): SourceRow.WriteRevisions {
    return {
      canonical: this.#canonicalCells.captureRevision(),
      local: this.#stores.sourceCellData.captureRevision(),
    };
  }

  /** The waited snapshot installed or bailed: let both stores prune. */
  #releaseWriteRevisions(since: SourceRow.WriteRevisions): void {
    this.#canonicalCells.releaseRevision(since.canonical);
    this.#stores.sourceCellData.releaseRevision(since.local);
  }

  /**
   * Run `fn` with structural-change emits coalesced to a single
   * `SheetStructureChanged` on drain (delegates to `TreeStructure.batchStructural`).
   * A structural swap (e.g. `replaceSystemRows`) otherwise emits one event per
   * tree mutation, each fanning out synchronously to `recomputeComputedSources`
   * + the UI effect set — the O(rows × dependents) rapid-switch freeze. Sibling
   * of `withDeferredListeners` (which coalesces the cell-listener side effects).
   */
  #withDeferredStructure<T>(fn: () => T): T {
    return this.#stores.tree.batchStructural(fn);
  }

  #drainDeferredListenerWork(): void {
    if (this.#listenerDrainObservers.size > 0) {
      for (const observer of [...this.#listenerDrainObservers]) observer();
    }
    if (this.#pendingAliasReexpansionRowIds.size > 0) {
      const rowIds = [...this.#pendingAliasReexpansionRowIds];
      this.#pendingAliasReexpansionRowIds.clear();
      for (const rowId of rowIds) {
        this.#reexpandAliasesForDynamicSource(rowId);
      }
    }
    this.#recomputes.drain();
  }

  #recomputeComputedDescendants(
    root: ModelRow,
    rangeOf: () => Option<NaiveDate.Range>,
  ): void {
    // Collect the computed targets BEFORE resolving the range: `rangeOf` is
    // the O(rows × cells) `observedRange` scan, and most subtrees carry no
    // computed rows at all.
    const targets: ComputedDataSource[] = [];
    if (root instanceof ComputedDataSource) targets.push(root);
    for (const id of collectDescendants(this.#stores.tree, root.id)) {
      const row = this.peekRow(id);
      if (row instanceof ComputedDataSource) targets.push(row);
    }
    if (targets.length === 0) return;
    const range = rangeOf();
    for (const row of targets) {
      this.#recomputeComputedRow(row, range);
    }
  }

  #recomputeComputedSources(range: Option<NaiveDate.Range>): void {
    if (this.#recomputeSweepObservers.size > 0) {
      for (const observer of [...this.#recomputeSweepObservers]) observer(range);
    }
    const t0 = performance.now();
    const tally = RecomputeTally.empty();
    for (const row of this.#rowsById.values()) {
      if (!(row instanceof ComputedDataSource)) continue;
      tally.count(this.#recomputeComputedRow(row, range));
    }
    // `sheet/recompute-sources` measures one whole-sheet computed-row pass
    // (runs once per system-rows replace): wall time, rows, memo hits/misses.
    // Emitted only past 50ms.
    tally.emit("sheet/recompute-sources", t0);
  }

  /** Open hydrate tree builds (`holdInsertRecomputes`); while positive an insert recomputes no parent. */
  #insertRecomputesHeld = 0;

  #holdInsertRecomputes(): () => void {
    this.#insertRecomputesHeld++;
    let released = false;
    return () => {
      if (released) return;
      released = true;
      this.#insertRecomputesHeld--;
    };
  }

  #recomputeComputedRow(
    row: ComputedDataSource,
    range: Option<NaiveDate.Range>,
  ): Option<ComputedDataSource.RecomputeOutcome> {
    if (range == null) return null;
    return row.recompute(range);
  }
}

/**
 * DFS collection of every descendant under `id`, deepest first.
 * Used by `Sheet.deleteRow` to enumerate the subtree that needs its
 * cells cleared — the tree is pruned *after* the walk so recursion
 * sees an intact structure.
 */
function collectDescendants(
  tree: SheetStores["tree"],
  id: RowLocalId,
): RowLocalId[] {
  const out: RowLocalId[] = [];
  const stack = [...tree.children(id)];
  for (let d = stack.pop(); d !== undefined; d = stack.pop()) {
    out.push(d);
    stack.push(...tree.children(d));
  }
  return out;
}

const SHAPE_TO_ENCODING: ReadonlyMap<string, string> = new Map([
  ["task/v1", "task.v1"],
  ["event/v1", "event.v1"],
  ["weather/v1", "weather.v1"],
  ["slot/v1", "slot.v1"],
]);

function shapeToEncoding(shape: string): Option<string> {
  return SHAPE_TO_ENCODING.get(shape) ?? null;
}

/** Types that go with `Sheet`: `AttrWrite`. */
export namespace Sheet {
  /**
   * The verdict of a display-attribute write (`setRendererDisplayAttr`, the
   * row-options seam): see `SheetRendererCow.AttrWrite`.
   */
  export type AttrWrite = SheetRendererCow.AttrWrite;
}
